import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { isAxiosError } from "axios"
import { useCallback, useEffect, useState } from "react"
import { setReauthHandler } from "@/shared/api/authenticated-client"
import { refreshToken } from "@/shared/api/refresh-token"
import { AUTH_ERROR_MESSAGES, AUTH_STORAGE_KEYS } from "@/shared/config/auth"
import { forgetTelegramInitData, storedTelegramInitData } from "@/shared/lib/telegram-mini-app"
import type { User } from "@/shared/lib/types"
import {
	authenticateCode,
	authenticateTelegram,
	authenticateTelegramWidget,
	type EmailCredentials,
	getCurrentUser,
	logoutRequest,
	type TelegramWidgetUser,
	useEmailAuth,
} from "../api/auth"

/** Бэкофис открыт только сотрудникам: обычная учётная запись каталога сюда не пускается. */
const hasBackofficeAccess = (user: User): boolean =>
	user.role === "ADMIN" || user.role === "MODERATOR"

const clearStoredSession = () => {
	sessionStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
	sessionStorage.removeItem(AUTH_STORAGE_KEYS.USER)
}

const storeUser = (user: User) => {
	sessionStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
}

/**
 * Сессия бэкофиса: вход по email и паролю, восстановление сессии на перезагрузке
 * (через access-токен в sessionStorage, а если его нет: через refresh-cookie),
 * вход без пароля, когда бэкофис открыт как Telegram Mini App, выход и реакция
 * на 401 от api-клиента.
 */
export const useAuth = () => {
	const [isRestoring, setIsRestoring] = useState(true)
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const [user, setUser] = useState<User | null>(null)
	const [error, setError] = useState<string | null>(null)
	const { mutateAsync: authenticate, isPending: isLoggingIn } = useEmailAuth()

	const acceptUser = useCallback((nextUser: User): boolean => {
		if (!hasBackofficeAccess(nextUser)) {
			clearStoredSession()
			setUser(null)
			setIsAuthenticated(false)
			setError(AUTH_ERROR_MESSAGES.FORBIDDEN)
			return false
		}

		storeUser(nextUser)
		setUser(nextUser)
		setIsAuthenticated(true)
		setError(null)
		return true
	}, [])

	const enterWithTelegram = useCallback(
		async (initData: string) => {
			try {
				const response = await authenticateTelegram(initData)
				sessionStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, response.accessToken)
				acceptUser(response.user ?? (await getCurrentUser()))
			} catch (telegramError) {
				// Подпись устарела или чужая: дальше только вход по email, иначе ошибка повторится.
				forgetTelegramInitData()
				setError(extractApiErrorMessage(telegramError, AUTH_ERROR_MESSAGES.AUTH_FAILED))
			}
		},
		[acceptUser],
	)

	const restore = useCallback(async () => {
		setIsRestoring(true)
		try {
			const renew = async () => {
				const refreshed = await refreshToken()
				sessionStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, refreshed.accessToken)
			}
			// Access-токен живёт в памяти вкладки, refresh: в httpOnly-cookie:
			// после перезагрузки страницы восстанавливаемся из неё.
			const hadToken = !!sessionStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
			if (!hadToken) await renew()

			let current: User
			try {
				current = await getCurrentUser()
			} catch (meError) {
				// Токен во вкладке мог истечь, пока она висела: cookie ещё живая, обновляемся по ней.
				if (!hadToken || !isAxiosError(meError) || meError.response?.status !== 401) throw meError
				await renew()
				current = await getCurrentUser()
			}
			acceptUser(current)
		} catch {
			clearStoredSession()
			setUser(null)
			setIsAuthenticated(false)
			const initData = storedTelegramInitData()
			if (initData) await enterWithTelegram(initData)
		} finally {
			setIsRestoring(false)
		}
	}, [acceptUser, enterWithTelegram])

	useEffect(() => {
		restore()
	}, [restore])

	useEffect(() => {
		setReauthHandler(() => {
			clearStoredSession()
			setUser(null)
			setIsAuthenticated(false)
		})
	}, [])

	/** Вход по email и паролю. Возвращает true, если сессия открыта. */
	const login = useCallback(
		async (credentials: EmailCredentials): Promise<boolean> => {
			setError(null)
			try {
				const response = await authenticate(credentials)
				return acceptUser(response.user ?? (await getCurrentUser()))
			} catch (loginError) {
				setError(extractApiErrorMessage(loginError, AUTH_ERROR_MESSAGES.AUTH_FAILED))
				return false
			}
		},
		[authenticate, acceptUser],
	)

	/** Перечитать профиль, например после привязки Telegram в боте. */
	const refreshUser = useCallback(async () => {
		try {
			acceptUser(await getCurrentUser())
		} catch {
			// Профиль обновится при следующем запросе, сессия от этого не страдает.
		}
	}, [acceptUser])

	/** Вход без пароля: по коду из бота или через виджет Telegram. */
	const [isEntering, setIsEntering] = useState(false)
	const enterWith = useCallback(
		async (request: () => ReturnType<typeof authenticateCode>): Promise<boolean> => {
			setError(null)
			setIsEntering(true)
			try {
				const response = await request()
				sessionStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, response.accessToken)
				return acceptUser(response.user ?? (await getCurrentUser()))
			} catch (loginError) {
				setError(extractApiErrorMessage(loginError, AUTH_ERROR_MESSAGES.AUTH_FAILED))
				return false
			} finally {
				setIsEntering(false)
			}
		},
		[acceptUser],
	)
	const loginWithCode = useCallback(
		(code: string) => enterWith(() => authenticateCode(code)),
		[enterWith],
	)
	const loginWithWidget = useCallback(
		(widget: TelegramWidgetUser) => enterWith(() => authenticateTelegramWidget(widget)),
		[enterWith],
	)

	/** Выход: гасим сессию на бэкенде и забываем токены во вкладке. */
	const logout = useCallback(async () => {
		try {
			await logoutRequest()
		} catch {
			// Сессия всё равно считается закрытой: refresh-cookie протухнет сама.
		}
		clearStoredSession()
		forgetTelegramInitData()
		setUser(null)
		setIsAuthenticated(false)
		setError(null)
	}, [])

	return {
		isRestoring,
		isAuthenticated,
		isLoggingIn: isLoggingIn || isEntering,
		user,
		error,
		login,
		loginWithCode,
		loginWithWidget,
		logout,
		refreshUser,
	}
}
